import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabase/supabase";
import agentServerRegions from "@/config/agent-server-regions.json";

// Loading and labels for the agent-server region pickers (assistant Region section,
// organization default region). The RPC returns one row per region with at least one
// registered server; the browser never reads voxi_agent_servers itself.

// One row of voxi_agent_regions().
interface RegionRow {
  region: string;
  live_servers: number;
  total_servers: number;
}

const regionNames = new Map(
  agentServerRegions.regions.map((r) => [r.id, r.name]),
);

// A code the list does not know is shown as the bare code.
export const regionName = (code: string) => regionNames.get(code) ?? code;
export const regionLabel = (code: string) =>
  regionNames.has(code) ? `${regionName(code)} (${code})` : code;

export function useAgentServerRegions() {
  // null until the RPC answers; stays null if it fails.
  const [rows, setRows] = useState<RegionRow[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc("voxi_agent_regions");
        if (cancelled) return;
        if (error) throw error;
        setRows(data ?? []);
        setLoadFailed(false);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load agent server regions:", error);
        setLoadFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Codes of the regions with a live server, sorted by label.
  const liveRegions = useMemo(
    () =>
      (rows ?? [])
        .filter((r) => r.live_servers > 0)
        .map((r) => r.region)
        .sort((a, b) => regionLabel(a).localeCompare(regionLabel(b))),
    [rows],
  );

  return { liveRegions, loaded: rows !== null, loadFailed };
}

// The organization's default region (organizations.default_agent_server_region).
// `loaded` is false while reading and after a failed read, so a caller that can do
// without the value just shows nothing extra. `region` is "" when none is set.
export function useOrganizationDefaultRegion(organizationId: string | undefined) {
  const [state, setState] = useState({ region: "", loaded: false });

  useEffect(() => {
    setState({ region: "", loaded: false });
    if (!organizationId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("organizations")
          .select("default_agent_server_region")
          .eq("id", organizationId)
          .maybeSingle();
        if (cancelled) return;
        if (error) throw error;
        setState({ region: data?.default_agent_server_region || "", loaded: !!data });
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load the organization default region:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  return state;
}

// The region codes to list for a picker. A saved region stays in the list even when
// nothing is running there, so opening a screen never changes it. This also covers
// loading and a failed load.
export function regionOptions(liveRegions: string[], saved: string): string[] {
  return saved && !liveRegions.includes(saved)
    ? [...liveRegions, saved]
    : liveRegions;
}

// True only when the list loaded and the region is not in it: a loading or failed
// load says nothing about whether a server is up.
export function hasNoLiveServer(
  loaded: boolean,
  liveRegions: string[],
  region: string,
): boolean {
  return loaded && region !== "" && !liveRegions.includes(region);
}
